import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form, Row, Col, Button } from 'react-bootstrap';
import { getSalesRepsWithLeadCount, getUsers, getClientOrigins, getRegistryLeadTypes, getLeadSubStatuses } from '../api';
import DatePicker from 'react-datepicker';
import MultiSalesRepSelector from './Global/MultiSalesRepSelector';
import { isPropietarioOrAdminOrGestionOrEspectador } from '../services/AuthService';

const LeadFilters = ({ onSearch, onResetFilters, showAllFilters = true, initialFilters = {}, collapsible = false }) => {
    const normalizeInitial = (filters) => ({
        zipCodes: Array.isArray(filters.zipCodes) ? filters.zipCodes.join(', ') : (filters.zipCodes || ''),
        phone: filters.phone || '',
        salesRepId: filters.salesRepId || [],
        originId: filters.originId || '',
        statusId: filters.statusId || '',
        subStatusId: filters.subStatusId || '',
        followUpDateFrom: filters.followUpDateFrom || '',
        followUpDateTo: filters.followUpDateTo || '',
        createdByUserId: filters.createdByUserId || '',
        createdDateFrom: filters.createdDateFrom || '',
        createdDateTo: filters.createdDateTo || '',
    });

    const initialNormalized = normalizeInitial(initialFilters);
    const [localFilters, setLocalFilters] = useState(initialNormalized);

    const [salesReps, setSalesReps] = useState([]);
    const [users, setUsers] = useState([]);
    const [clientOrigins, setClientOrigins] = useState([]);
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [leadSubStatuses, setLeadSubStatuses] = useState([]);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const searchTimeoutRef = useRef(null);
    const lastFiltersRef = useRef(JSON.stringify(initialNormalized));

    useEffect(() => {
        const loadData = async () => {
            try {
                const promises = [
                    getClientOrigins(),
                    getRegistryLeadTypes()
                ];
                
                if (isPropietarioOrAdminOrGestionOrEspectador()) {
                    promises.unshift(getSalesRepsWithLeadCount(), getUsers(true));
                }
                
                const results = await Promise.all(promises);
                
                if (isPropietarioOrAdminOrGestionOrEspectador()) {
                    setSalesReps(results[0]);
                    setUsers(results[1]);
                    setClientOrigins(results[2] || []);
                    setLeadStatuses(results[3] || []);
                } else {
                    setClientOrigins(results[0] || []);
                    setLeadStatuses(results[1] || []);
                }
                
                setIsInitialized(true);
            } catch (error) {
                console.error('Error loading filter data:', error);
            }
        };
        loadData();
    }, []);

    const handleChange = async (e) => {
        const { name, value } = e.target;
        setLocalFilters(prev => ({ ...prev, [name]: value }));
        
        if (name === 'statusId' && value) {
            try {
                const subStatusesData = await getLeadSubStatuses(value);
                setLeadSubStatuses(subStatusesData || []);
                setLocalFilters(prev => ({ ...prev, subStatusId: '' }));
            } catch (error) {
                console.error('Error loading sub statuses:', error);
                setLeadSubStatuses([]);
            }
        } else if (name === 'statusId' && !value) {
            setLeadSubStatuses([]);
            setLocalFilters(prev => ({ ...prev, subStatusId: '' }));
        }
    };

    const handleDateChange = (field, value) => {
        const formattedDate = value ? value.toISOString().split('T')[0] : '';
        setLocalFilters(prev => ({ ...prev, [field]: formattedDate }));
    };

    const triggerSearch = useCallback((filters) => {
        const zipCodes = Array.isArray(filters.zipCodes)
            ? filters.zipCodes
            : filters.zipCodes
                ? filters.zipCodes.split(',').map(code => code.trim()).filter(Boolean)
                : [];
        onSearch({ ...filters, zipCodes });
    }, [onSearch]);

    useEffect(() => {
        if (!isInitialized) return;
        
        const currentFilters = JSON.stringify(localFilters);
        if (currentFilters === lastFiltersRef.current) return;
        
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        
        const prev = lastFiltersRef.current ? JSON.parse(lastFiltersRef.current) : null;
        lastFiltersRef.current = currentFilters;
        const zipCodesChanged = !prev || prev.zipCodes !== localFilters.zipCodes;
        
        if (!zipCodesChanged) {
            triggerSearch(localFilters);
            return;
        }
        
        searchTimeoutRef.current = setTimeout(() => triggerSearch(localFilters), 500);
        
        return () => clearTimeout(searchTimeoutRef.current);
    }, [localFilters, isInitialized, triggerSearch]);

    const resetFilters = () => {
        setLocalFilters({
            zipCodes: '',
            phone: '',
            salesRepId: [],
            originId: '',
            statusId: '',
            subStatusId: '',
            followUpDateFrom: '',
            followUpDateTo: '',
            createdByUserId: '',
            createdDateFrom: '',
            createdDateTo: ''
        });
        setLeadSubStatuses([]);
        onResetFilters && onResetFilters();
    };

    return (
        <Form>
            {collapsible && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setIsCollapsed(!isCollapsed)}
                            className="d-flex align-items-center"
                        >
                            <i className={`bi bi-chevron-${isCollapsed ? 'right' : 'down'} me-2`}></i>
                            {isCollapsed ? 'Mostrar Filtros' : 'Ocultar Filtros'}
                        </Button>
                    </Col>
                </Row>
            )}
            {(!collapsible || !isCollapsed) && (
            <>
            <Row>
                {showAllFilters && (
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label>Códigos Postales</Form.Label>
                            <Form.Control
                                type="text"
                                name="zipCodes"
                                value={localFilters.zipCodes}
                                onChange={handleChange}
                                placeholder="Ej: 28001, 28002, 280"
                            />
                            <Form.Text className="text-muted">
                                Separar por comas. Busca por inicio del código.
                            </Form.Text>
                        </Form.Group>
                    </Col>
                )}
                <Col md={showAllFilters ? 3 : 4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Teléfono</Form.Label>
                        <Form.Control
                            type="text"
                            name="phone"
                            value={localFilters.phone}
                            onChange={handleChange}
                            placeholder="Número de teléfono"
                        />
                    </Form.Group>
                </Col>
                {!isPropietarioOrAdminOrGestionOrEspectador() ? null : (
                    <Col md={showAllFilters ? 3 : 4}>
                        <MultiSalesRepSelector
                            salesReps={salesReps}
                            selectedIds={localFilters.salesRepId}
                            onChange={(ids) => setLocalFilters(prev => ({ ...prev, salesRepId: ids }))}
                            label="Comercial"
                            placeholder="Todos los comerciales"
                        />
                    </Col>
                )}
                {showAllFilters && (
                    <Col md={3}>
                        <Form.Group className="mb-3">
                            <Form.Label>Origen</Form.Label>
                            <Form.Select
                                name="originId"
                                value={localFilters.originId}
                                onChange={handleChange}
                            >
                                <option value="">Todos</option>
                                {clientOrigins.map(origin => (
                                    <option key={origin.id} value={origin.valueId}>
                                        {origin.name}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}
            </Row>
            
            <Row>
                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label>Estado</Form.Label>
                        <Form.Select
                            name="statusId"
                            value={localFilters.statusId}
                            onChange={handleChange}
                        >
                            <option value="">Todos</option>
                            {leadStatuses.map(status => (
                                <option key={status.valueId} value={status.valueId}>
                                    {status.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label>Detalle de Estado</Form.Label>
                        <Form.Select
                            name="subStatusId"
                            value={localFilters.subStatusId}
                            onChange={handleChange}
                            disabled={!localFilters.statusId || leadSubStatuses.length === 0}
                        >
                            <option value="">Todos</option>
                            {leadSubStatuses.map(subStatus => (
                                <option key={subStatus.valueId} value={subStatus.valueId}>
                                    {subStatus.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Fecha de Seguimiento</Form.Label>
                        <Row>
                            <Col>
                                <DatePicker
                                    selected={localFilters.followUpDateFrom ? new Date(localFilters.followUpDateFrom) : null}
                                    onChange={(date) => handleDateChange('followUpDateFrom', date)}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Desde"
                                    className="form-control"
                                    todayButton="Hoy"
                                    showPopperArrow={false}
                                />
                            </Col>
                            <Col>
                                <DatePicker
                                    selected={localFilters.followUpDateTo ? new Date(localFilters.followUpDateTo) : null}
                                    onChange={(date) => handleDateChange('followUpDateTo', date)}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="Hasta"
                                    className="form-control"
                                    todayButton="Hoy"
                                    showPopperArrow={false}
                                />
                            </Col>
                        </Row>
                    </Form.Group>
                </Col>
            </Row>
            
            {showAllFilters && (
                <>
                    <Row>
                        {isPropietarioOrAdminOrGestionOrEspectador() && (
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Creado por</Form.Label>
                                    <Form.Select
                                        name="createdByUserId"
                                        value={localFilters.createdByUserId}
                                        onChange={handleChange}
                                    >
                                        <option value="">Todos</option>
                                        {users.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.username}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        )}
                        <Col md={isPropietarioOrAdminOrGestionOrEspectador() ? 6 : 12}>
                            <Form.Group className="mb-3">
                                <Form.Label>Fecha de Creación</Form.Label>
                                <Row>
                                    <Col>
                                        <DatePicker
                                            selected={localFilters.createdDateFrom ? new Date(localFilters.createdDateFrom) : null}
                                            onChange={(date) => handleDateChange('createdDateFrom', date)}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="Desde"
                                            className="form-control"
                                            todayButton="Hoy"
                                            showPopperArrow={false}
                                        />
                                    </Col>
                                    <Col>
                                        <DatePicker
                                            selected={localFilters.createdDateTo ? new Date(localFilters.createdDateTo) : null}
                                            onChange={(date) => handleDateChange('createdDateTo', date)}
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText="Hasta"
                                            className="form-control"
                                            todayButton="Hoy"
                                            showPopperArrow={false}
                                        />
                                    </Col>
                                </Row>
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            )}
            
            <Row>
                <Col xs={12} className="text-end">
                    <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={resetFilters}
                    >
                        Reiniciar Filtros
                    </Button>
                </Col>
            </Row>
            </>
            )}
        </Form>
    );
};

export default LeadFilters;